import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import FloatingContactIcons from '../../components/feature/FloatingContactIcons';
import HubSpotForm from '../../components/common/HubSpotForm';
import { api } from '../../lib/api';
import { useContentManager } from '../../hooks/useContentManager';

const ContactPage = () => {
  const { getContentItem, content, loading } = useContentManager();
  
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    email: '',
    subject: '',
    message: ''
  });
  const [countryCode, setCountryCode] = useState('+233');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);

  // Admin panelden verileri al
  const heroTitle = getContentItem('contact', 'hero-title');
  const heroSubtitle = getContentItem('contact', 'hero-subtitle');
  const heroBgImage = getContentItem('contact', 'hero-bg-image');
  const heroVideoItem = getContentItem('contact', 'hero-video');
  const formBgImage = getContentItem('contact', 'form-bg-image');
  // Separate left section content
  const leftTitle = getContentItem('contact', 'left-title');
  const leftSubtitle = getContentItem('contact', 'left-subtitle');
  
  // İletişim bilgilerini al
  const contactInfoItems = content.filter(item => 
    item.page === 'contact' && 
    item.section === 'contact-info'
  ).sort((a, b) => (a.order || 0) - (b.order || 0));

  // Quick Actions
  const quickActions = getContentItem('contact', 'quick-actions');
  
  // Map Section
  const mapSection = getContentItem('contact', 'map-section');
  
  // Office Locations
  const officeLocations = content.filter(item => 
    item.page === 'contact' && 
    item.section === 'office-locations'
  ).sort((a, b) => (a.order || 0) - (b.order || 0));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await api.post('/forms', {
        form_type: 'contact',
        name: formData.name,
        mobile: `${countryCode}${formData.mobile}`,
        email: formData.email,
        subject: formData.subject,
        message: formData.message,
        country_code: countryCode,
        status: 'new'
      });

      setSubmitStatus('success');
      setFormData({ name: '', mobile: '', email: '', subject: '', message: '' });
      setTimeout(() => setSubmitStatus(null), 3000);
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  // Varsayılan değerler
  const defaultHeroTitle = 'GET IN\nTOUCH';
  const defaultHeroSubtitle = 'Ready to discover your dream home? Our expert team is here to guide you through every step of your luxury living journey.';
  const defaultBgImage = 'https://readdy.ai/api/search-image?query=modern%20luxury%20skyscraper%20building%20at%20night%20with%20dramatic%20lighting%20and%20city%20skyline%2C%20architectural%20photography%2C%20dark%20moody%20atmosphere%2C%20professional%20real%20estate%20photography&width=1920&height=1080&seq=contact-bg-001&orientation=landscape';

  const heroTitleLines = (heroTitle?.content || defaultHeroTitle).split('\n');
  const heroSubtitleText = (heroSubtitle?.content || '').toString();
  const leftTitleLines = (leftTitle?.content || heroTitle?.content || defaultHeroTitle).split('\n');
  const leftSubtitleText = leftSubtitle?.content || heroSubtitle?.content || defaultHeroSubtitle;
  const bgImageUrl = heroBgImage?.image_url || heroBgImage?.content || defaultBgImage;
  const heroVideoUrl = (heroVideoItem?.video_url as string | undefined) || (heroVideoItem?.content as string | undefined) || '';

  return (
    <div className="min-h-screen bg-black text-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative min-h-screen bg-[#2a2a35] text-white overflow-hidden">
        {/* Background Media */}
        <div className="absolute inset-0">
          {heroVideoUrl ? (
            <video
              src={heroVideoUrl}
              autoPlay
              muted
              loop
              playsInline
              className="w-full h-full object-cover"
            />
          ) : (
            <img
              src={bgImageUrl}
              alt="Background"
              className="w-full h-full object-cover"
            />
          )}
        </div>
        <div className="absolute inset-0 bg-black/40"></div>

        <div className="absolute inset-0 z-10 flex items-center justify-center">
          <div className="relative z-10 flex flex-col justify-center items-center h-full text-center px-4">
            <h1 className="text-5xl lg:text-7xl font-light tracking-wide text-white mb-4">
              {heroTitleLines.map((line, index) => (
                <span key={index}>
                  {line}
                  {index !== heroTitleLines.length - 1 && <br />}
                </span>
              ))}
            </h1>
            {heroSubtitleText.trim() && (
              <p className="max-w-3xl mx-auto text-base md:text-lg text-white/80 mb-6">{heroSubtitleText}</p>
            )}
          </div>
        </div>
      </section>

      {/* Office Locations */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-light text-center mb-12">
            Our <span className="font-bold text-[#C5A46D]">Locations</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {officeLocations.length > 0 ? (
              officeLocations.map((office) => (
                <div key={office.id} className="bg-gray-900 p-8 rounded-lg text-center">
                  <div className="w-16 h-16 bg-[#C5A46D] rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className={`${office.metadata?.icon || 'ri-building-line'} text-black text-2xl`}></i>
                  </div>
                  <h3 className="text-xl font-medium mb-4">{office.metadata?.title}</h3>
                  <p className="text-gray-300 mb-4" dangerouslySetInnerHTML={{ __html: office.metadata?.address?.replace(/\n/g, '<br />') || '' }}></p>
                  <p className="text-sm text-gray-400 mb-4">
                    {office.metadata?.hours}
                  </p>
                  {office.metadata?.phone && (
                    <a
                      href={`tel:${office.metadata.phone}`}
                      className="text-[#C5A46D] hover:text-white transition-colors"
                    >
                      {office.metadata.phone}
                    </a>
                  )}
                  {office.metadata?.email && (
                    <a
                      href={`mailto:${office.metadata.email}`}
                      className="text-[#C5A46D] hover:text-white transition-colors block mt-2"
                    >
                      {office.metadata.email}
                    </a>
                  )}
                </div>
              ))
            ) : (
              <>
                <div className="bg-gray-900 p-8 rounded-lg text-center">
                  <div className="w-16 h-16 bg-[#C5A46D] rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="ri-building-line text-black text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-medium mb-4">Head Office</h3>
                  <p className="text-gray-300 mb-4">
                    Business Bay<br />
                    Dubai, UAE
                  </p>
                  <p className="text-sm text-gray-400 mb-4">
                    Mon - Sun: 9:00 AM - 8:00 PM
                  </p>
                  <a
                    href="tel:+97141234567"
                    className="text-[#C5A46D] hover:text-white transition-colors"
                  >
                    +971 4 123 4567
                  </a>
                </div>

                <div className="bg-gray-900 p-8 rounded-lg text-center">
                  <div className="w-16 h-16 bg-[#C5A46D] rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="ri-home-line text-black text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-medium mb-4">Sales Office</h3>
                  <p className="text-gray-300 mb-4">
                    Downtown Dubai<br />
                    Dubai, UAE
                  </p>
                  <p className="text-sm text-gray-400 mb-4">
                    Mon - Sun: 10:00 AM - 9:00 PM
                  </p>
                  <a
                    href="tel:+97141234568"
                    className="text-[#C5A46D] hover:text-white transition-colors"
                  >
                    +971 4 123 4568
                  </a>
                </div>

                <div className="bg-gray-900 p-8 rounded-lg text-center">
                  <div className="w-16 h-16 bg-[#C5A46D] rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="ri-customer-service-line text-black text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-medium mb-4">Customer Service</h3>
                  <p className="text-gray-300 mb-4">
                    24/7 Support<br />
                    Available Online
                  </p>
                  <p className="text-sm text-gray-400 mb-4">
                    24 Hours / 7 Days
                  </p>
                  <a
                    href="mailto:support@goldentower.com"
                    className="text-[#C5A46D] hover:text-white transition-colors"
                  >
                    support@goldentower.com
                  </a>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-light text-center mb-12">
            {mapSection?.metadata?.title || 'Visit Our'} <span className="font-bold text-[#C5A46D]">{mapSection?.metadata?.title_highlight || 'Office'}</span>
          </h2>
          <div className="w-full h-[500px] rounded-lg overflow-hidden">
            <iframe
              src={mapSection?.metadata?.map_url || 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3610.1739405345!2d55.26715!3d25.1972!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f69d4d4d4d4d4%3A0x1234567890abcdef!2sBusiness%20Bay%2C%20Dubai%2C%20UAE!5e0!3m2!1sen!2sae!4v1234567890123!5m2!1sen!2sae'}
              width="100%"
              height="100%"
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Office Location"
              className="border-0"
            />
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-[#2a2a35]">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Left column: texts & infos */}
            <div className="space-y-8">
              <div className="space-y-4">
                {leftTitleLines.map((line, index) => (
                  <h1 key={index} className="text-5xl lg:text-6xl font-light tracking-wide">
                    {index === leftTitleLines.length - 1 ? (
                      <span className="text-[#C5A46D]">{line}</span>
                    ) : (
                      line
                    )}
                  </h1>
                ))}
              </div>
              <p className="text-xl text-gray-300 leading-relaxed">
                {leftSubtitleText}
              </p>
              <div className="space-y-6 pt-8">
                {contactInfoItems.length > 0 ? (
                  contactInfoItems.map((item) => (
                    <div key={item.id} className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full border border-[#C5A46D] flex items-center justify-center">
                        <i className={`${item.metadata?.icon || 'ri-phone-line'} text-[#C5A46D] text-xl`}></i>
                      </div>
                      <div>
                        <h3 className="text-lg font-medium">{item.metadata?.title}</h3>
                        <p className="text-gray-300">{item.metadata?.value}</p>
                      </div>
                    </div>
                  ))
                ) : null}
              </div>
              <div className="flex gap-4 pt-4">
                {quickActions?.metadata?.call_url && (
                  <a href={quickActions.metadata.call_url} className="bg-[#C5A46D] text-black px-6 py-3 rounded-full font-medium hover:bg-[#B8956A] transition-colors duration-300 cursor-pointer">
                    {quickActions?.metadata?.call_text || 'Call Now'}
                  </a>
                )}
                {quickActions?.metadata?.whatsapp_url && (
                  <a href={quickActions.metadata.whatsapp_url} className="border border-[#C5A46D] text-[#C5A46D] px-6 py-3 rounded-full font-medium hover:bg-[#C5A46D] hover:text-black transition-colors duration-300 cursor-pointer">
                    {quickActions?.metadata?.whatsapp_text || 'WhatsApp'}
                  </a>
                )}
              </div>
            </div>

            {/* Right column: form with admin background */}
            <div
              className="rounded-lg p-8 lg:p-12 bg-[#3a3a45]/60 backdrop-blur-sm"
              style={{
                backgroundImage: (formBgImage?.image_url || formBgImage?.content) ? `url(${formBgImage?.image_url || formBgImage?.content})` : undefined,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            >
              <h2 className="text-2xl font-light mb-8">
                Send us a <span className="font-bold text-[#C5A46D]">Message</span>
              </h2>
              <HubSpotForm />
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
      
      {/* Floating Contact Icons */}
      <FloatingContactIcons />
    </div>
  );
};

export default ContactPage;
